import React, { useContext, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { getUserTrips } from "../../services/userService";
import PaymentHistory from "../payment/PaymentHistory";
import { getPassengerById } from "../../services/passengerService";
import { processErrorResponse } from "../../helpers/processErrorResponse";
import { useSelector } from "react-redux";
import useMobile from "../../hooks/useOrientation";
import { getPaymentHistory } from "../../services/paymentService";
import { CenteredLoader } from "../shared/CenteredLoader";
import { PassengerTripDetails } from "../payment/PassengerTripDetails";
import { useLocation, useNavigate } from "react-router-dom";
import { getSessionCookie, SessionContext } from "../../helpers/session";
import { jwtDecode } from "jwt-decode";
import Bugsnag from "@bugsnag/browser";
import { primary } from "../../ui/colors";

const primaryLight = "#e0faf7";
const primaryDark = "#00a896";

const getInitials = (name = "", lastName = "") =>
  `${name.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const MyTrips = () => {
  const [trips, setTrips] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState();
  const [error, setError] = useState();

  const navigate = useNavigate();
  const { state } = useLocation();
  const p = state?.p;
  const { user } = useContext(SessionContext);
  const id = user?.id;
  const { passengers } = useSelector((s) => s.passengers);
  const isMobile = useMobile();

  useEffect(() => {
    setIsLoading(true);
    Bugsnag.notify("Ver mis viajes.", function (event) {
      event.severity = "info";
      const { UserID, Name, LastName, Email } = jwtDecode(getSessionCookie());
      event.setUser(UserID, Email, Name + " " + LastName);
    });
    if (id) {
      getUserTrips(id)
        .then((res) => {
          setTrips(res);
          if (res.length === 1) {
            setSelectedTrip(res[0]);
          } else if (p) {
            const preselected = res.find((r) => r.passengerId === p.id);
            if (preselected) setSelectedTrip(preselected);
          }
          setIsLoading(false);
        })
        .catch((e) => {
          setIsLoading(false);
          setError(processErrorResponse(e)[1]);
        });
    }
  }, []);

  const handleSelectPassenger = (passengerId) => {
    if (!trips) return;
    const trip = trips.find((t) => t.passengerId === passengerId);
    setSelectedTrip(trip);
  };

  if (isLoading) {
    return (
      <div style={styles.loaderWrapper}>
        <CircularProgress style={{ color: primary }} />
      </div>
    );
  }

  if (error) {
    return <h3 style={{ padding: 16, color: "#c0392b" }}>{error}</h3>;
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? "16px 12px" : "24px 20px" }}>
      {/* Page header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Mis viajes</h1>
        <button style={styles.btnOutline} onClick={() => navigate("/registro")}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Agregar viaje
        </button>
      </div>

      {/* Passenger chips */}
      {passengers && passengers.length > 0 && (
        <div style={styles.chipsRow}>
          {passengers.map((pass) => {
            const isActive = selectedTrip?.passengerId === pass.id;
            return (
              <button
                key={pass.id}
                style={{
                  ...styles.chip,
                  ...(isActive ? styles.chipActive : {}),
                }}
                onClick={() => handleSelectPassenger(pass.id)}
              >
                <div style={styles.chipAvatar}>{getInitials(pass.name, pass.lastName)}</div>
                <div style={styles.chipInfo}>
                  <span style={styles.chipName}>{pass.name} {pass.lastName}</span>
                  <span style={styles.chipTrip}>{pass.programName}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected trip */}
      {selectedTrip ? (
        <div style={{ minHeight: "50vh" }}>
          <SelectedTrip trip={selectedTrip} isMobile={isMobile} />
        </div>
      ) : (
        passengers?.length > 0 && (
          <p style={{ color: "#888", marginTop: 12 }}>Seleccioná un pasajero para ver los detalles.</p>
        )
      )}
    </div>
  );
};

export default MyTrips;

// ---- SelectedTrip ----

const SelectedTrip = ({ trip, isMobile }) => {
  const [passenger, setPassenger] = useState();
  const [paymentHistory, setPaymentHistory] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setPaymentHistory(undefined);
    setPassenger(undefined);

    getPassengerById(trip.passengerId).then((res) => setPassenger(res));

    getPaymentHistory(trip.passengerId)
      .then((res) => {
        setPaymentHistory(res.filter((s) => s.status !== "Delete"));
      })
      .catch((e) => processErrorResponse(e));
  }, [trip]);

  if (!passenger || !paymentHistory) {
    return <CenteredLoader />;
  }

  return (
    <div style={{ marginTop: 8 }}>
      <PassengerTripDetails
        setFinishedLoading={() => setLoading(false)}
        passenger={passenger}
      />
      {!loading && (
        <div style={{ marginTop: isMobile ? 8 : 4 }}>
          <PaymentHistory
            paymentHistory={paymentHistory}
            currency={passenger.currency}
          />
        </div>
      )}
    </div>
  );
};

// ---- Styles ----

const styles = {
  container: {
    maxWidth: 780,
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "60vh",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
  },
  btnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    border: `1.5px solid ${primary}`,
    borderRadius: 8,
    background: "transparent",
    color: primaryDark,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Gilroy",
    flexShrink: 0,
  },
  chipsRow: {
    display: "flex",
    gap: 10,
    overflowX: "auto",
    paddingBottom: 4,
    marginBottom: 24,
    WebkitOverflowScrolling: "touch",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    flexShrink: 0,
    fontFamily: "Gilroy",
    transition: "border-color 0.15s, background 0.15s",
  },
  chipActive: {
    borderColor: primary,
    background: primaryLight,
  },
  chipAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 12,
    color: "#fff",
    flexShrink: 0,
  },
  chipInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  chipName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111",
  },
  chipTrip: {
    fontSize: 13,
    color: primaryDark,
    fontWeight: 500,
    marginTop: 1,
  },
};
